import React, { useState } from 'react';
import { View } from 'react-native';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramSensibleHeat } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { useUnits } from '../../../context/UnitsContext';

const KEY = 'tabai.calc.heatTransferSensible.v1';

type Fields = { airFlow: string; tempDiff: string; sensibleHeat: string };
const EMPTY: Fields = { airFlow: '', tempDiff: '', sensibleHeat: '' };

/**
 * Sensible Heat Transfer — Q_s = 1.08 × CFM × ΔT
 * Solves for whichever of CFM / ΔT / Q_s is missing given the other two.
 * ΔT is a temperature difference — no offset conversion needed, just scaling.
 */
const HeatTransferSensibleHeatTransferScreen = () => {
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);
  const { unit, toImp, fromImp, isImperial } = useUnits();

  const set = (patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch }));

  // ΔT conversion: scale only (no offset), 1°F = 5/9 °C difference
  const dtToImp = (s: string) => {
    const n = parseFloat(s);
    if (!Number.isFinite(n)) return NaN;
    return isImperial ? n : n * 9 / 5;
  };
  const dtFromImp = (n: number, dec = 1) => {
    if (!Number.isFinite(n)) return '';
    return parseFloat((isImperial ? n : n * 5 / 9).toFixed(dec)).toString();
  };
  const dtUnit = isImperial ? '°F' : '°C';

  const calculate = () => {
    setError(null);
    const filled = (Object.values(fields) as string[]).filter((v) => v !== '').length;
    if (filled < 2) {
      setError('Fill any 2 of the 3 fields to solve for the missing one.');
      return;
    }
    if (filled === 3) {
      setError('All 3 values are filled — clear one to solve for it.');
      return;
    }

    const cfm = toImp(fields.airFlow, 'flow');
    const dt = dtToImp(fields.tempDiff);
    const qs = toImp(fields.sensibleHeat, 'heatRate');

    if (fields.sensibleHeat === '') {
      if (cfm <= 0) { setError('Air flow must be greater than zero.'); return; }
      setSolvedFor('sensibleHeat');
      set({ sensibleHeat: fromImp(1.08 * cfm * dt, 'heatRate', 0) });
    } else if (fields.airFlow === '') {
      if (dt === 0) { setError('Temperature difference cannot be zero.'); return; }
      setSolvedFor('airFlow');
      set({ airFlow: fromImp(qs / (1.08 * dt), 'flow', 1) });
    } else if (fields.tempDiff === '') {
      if (cfm <= 0) { setError('Air flow must be greater than zero.'); return; }
      setSolvedFor('tempDiff');
      set({ tempDiff: dtFromImp(qs / (1.08 * cfm)) });
    }
  };

  const clearAll = () => {
    setError(null);
    setSolvedFor(null);
    void clearPersisted();
  };

  const result = [
    fields.airFlow && { label: 'Air Flow', value: fields.airFlow, unit: unit('flow'), emphasis: solvedFor === 'airFlow' },
    fields.tempDiff && { label: 'Temperature Difference', value: fields.tempDiff, unit: dtUnit, emphasis: solvedFor === 'tempDiff' },
    fields.sensibleHeat && { label: 'Sensible Heat Transfer', value: fields.sensibleHeat, unit: unit('heatRate'), emphasis: solvedFor === 'sensibleHeat' },
  ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[];

  return (
    <CalcShell
      title="Sensible Heat Transfer"
      subtitle="Calculate sensible heat transfer rate from air flow and temperature difference"
      formula={
        <>
          <FormulaLine>{isImperial ? 'Q_s = 1.08 × CFM × ΔT' : 'Q_s = 1.21 × L/s × ΔT'}</FormulaLine>
          <FormulaNote>Q_s = Sensible Heat Transfer ({unit('heatRate')}), {isImperial ? 'CFM' : 'L/s'} = Air Flow Rate ({unit('flow')}), ΔT = Temperature Difference ({dtUnit})</FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      copyFooter={isImperial ? 'Formula: Q_s = 1.08 × CFM × ΔT' : 'Formula: Q_s = 1.21 × L/s × ΔT'}
      diagram={<DiagramSensibleHeat />}
      outputSlots={{
        cfm: fields.airFlow ? toImp(fields.airFlow, 'flow') : undefined,
        delta_t_f: fields.tempDiff ? dtToImp(fields.tempDiff) : undefined,
        sensible_heat_btuh: fields.sensibleHeat ? toImp(fields.sensibleHeat, 'heatRate') : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.cfm != null && !fields.airFlow) patch.airFlow = fromImp(values.cfm, 'flow', 1);
        if (values.delta_t_f != null && !fields.tempDiff) patch.tempDiff = dtFromImp(values.delta_t_f);
        if (values.sensible_heat_btuh != null && !fields.sensibleHeat) patch.sensibleHeat = fromImp(values.sensible_heat_btuh, 'heatRate', 0);
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Air Flow"
          value={fields.airFlow}
          onChangeText={(v) => set({ airFlow: v })}
          rightText={unit('flow')}
          maxDecimals={1}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Temperature Difference"
          value={fields.tempDiff}
          onChangeText={(v) => set({ tempDiff: v })}
          rightText={dtUnit}
          maxDecimals={1}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Sensible Heat Transfer"
          value={fields.sensibleHeat}
          onChangeText={(v) => set({ sensibleHeat: v })}
          rightText={unit('heatRate')}
          maxDecimals={0}
          onSubmitEditing={calculate}
        />
      </View>
    </CalcShell>
  );
};

export default HeatTransferSensibleHeatTransferScreen;
